


import { MetadataEntityData } from '../decorators/model/metadata.entity.data';
import { MetadataEntityFieldData } from '../decorators/model/metadata.entity.field.data';
import { MetadataEntityFieldType } from '../decorators/model/metadata.entity.field.types';
import { MetadataImportValidationCell, MetadataImportValidationData, MetadataImportValidationRow } from './metadata.validation.data';
import { MetadataFieldMapper } from './metadata.field.mapper';
import { MetadataImportFieldBuilder } from './metadata.import.field.builder';

import * as Enumerable from 'linq';
import { ActivityEntityName } from '../../../entities/shared/activity.entity.name';
import { SecurityModuleName } from '../../../security/security.module';
import { MetadataImportConfig } from './metadata.import.config';

export type MappingPreset = { source: string, destination: string };

export class MetadataImportData {

    public readonly destinationModule: SecurityModuleName;
    public readonly destinationEntity: ActivityEntityName;
    public readonly destinationFields: MetadataEntityFieldData[];

    public readonly mapper: MetadataFieldMapper;

    public readonly sourceRows: any[][];

    public readonly duplicateRowCount: number;
    public readonly duplicateHints: any[];


    public constructor(
        public readonly destination: MetadataEntityData,
        public readonly sourceHeaders: any[],
        public readonly sourceName: string,
        sourceRows: any[][],
        mappingPreset?: MappingPreset[],
        private config?: MetadataImportConfig
    ) {

        const deDupe = this.removeDuplicateRows(sourceRows);

        this.sourceRows = deDupe.rows;
        this.duplicateRowCount = deDupe.duplicateCount;
        this.duplicateHints = deDupe.duplicateHints;

        this.destinationModule = destination.module;
        this.destinationEntity = destination.type;
        this.destinationFields = MetadataImportFieldBuilder.getAvailableFields(this.destination);
        this.mapper = new MetadataFieldMapper(this.destinationFields);

        if (mappingPreset?.length) {

            const sourceHeaderMap = Enumerable.from(sourceHeaders).toDictionary(x => x);
            const destFieldMap = Enumerable.from(this.destinationFields).toDictionary(x => x.path, x => x);

            Enumerable
                .from(mappingPreset)
                .where(x => {
                    return sourceHeaderMap.contains(x.source) && destFieldMap.contains(x.destination);
                })
                .forEach(x => {
                    this.mapper.addMapping(x.source, destFieldMap.get(x.destination));
                });

        }

    }

    public toValidation(): MetadataImportValidationData {

        const indexes = [];

        const headers = Enumerable
            .from(this.sourceHeaders)
            .where(header => this.mapper.mappings.has(header))
            .select((header, i) => {
                indexes.push(i);
                return this.mapper.mappings.get(header);
            })
            .toArray();

        const actionHeader = {
            type: MetadataEntityFieldType._Action_,
            name: MetadataEntityFieldType._Action_
        } as MetadataEntityFieldData;

        const validation = new MetadataImportValidationData(
            this.sourceName,
            this.destinationModule,
            this.destinationEntity,
            [actionHeader, ...headers],
            this.config
        );

        Enumerable
            .from(this.sourceRows)
            .forEach((row) => {

                const cells = [
                    new MetadataImportValidationCell('Include', actionHeader, this.config)
                ];

                Enumerable
                    .from(this.sourceHeaders)
                    .forEach((header, i) => {
                        if (this.mapper.mappings.has(header)) {
                            const headerField = this.mapper.mappings.get(header);
                            cells.push(new MetadataImportValidationCell(row[i], headerField, this.config));
                        }
                    })

                const validationRow = new MetadataImportValidationRow(cells);
                validation.addRow(validationRow)

            });


        return validation;
    }

    private removeDuplicateRows(source: any[][]) {

        const duplicateHints = [];

        const rows = Enumerable
            .from(source)
            .select((row, index) => {
                const hash = row.join(':').toLowerCase();
                return {
                    hash,
                    index,
                    row
                }
            })
            .groupBy(x => x.hash)
            .select(x => {
                const first = x.firstOrDefault();

                if (x.count() > 1) {
                    duplicateHints.push(x.skip(1).select(d => `Row #${d.index + 1}, starting with: '${first?.row[0]}' was removed because it was an exact duplicate of Row #${first.index + 1}`).toArray());
                }

                return first;

            })
            .select(x => x.row)
            .toArray();

        return {
            duplicateCount: source?.length - rows?.length,
            duplicateHints,
            rows
        }
    }

}